import { SignInIdentifier, type PasswordVerificationPayload } from '@logto/schemas';
import { useCallback, useContext, useState } from 'react';

import CaptchaContext, {
  captchaCancelled,
} from '@/Providers/CaptchaContextProvider/CaptchaContext';
import { signInWithPasswordIdentifier } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useCheckSingleSignOn from '@/hooks/use-check-single-sign-on';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';

/**
 * 上游 usePasswordSignIn 的 EVA 变体:逻辑一致(captcha → SSO 检查 → 密码登录),
 * 唯一区别是**所有错误都内联红字展示**(global → setErrorMessage),
 * 不走上游的 toast/alert —— 与 EvaPhoneCodeSignIn 的反馈方式保持一致。
 */
const useEvaPasswordSignIn = () => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const { onSubmit: checkSingleSignOn } = useCheckSingleSignOn();
  const redirectTo = useGlobalRedirectTo();
  const { executeCaptcha } = useContext(CaptchaContext);
  const asyncSignIn = useApi(signInWithPasswordIdentifier);
  const handleError = useErrorHandler();

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const onSubmit = useCallback(
    async (payload: PasswordVerificationPayload) => {
      const { identifier } = payload;
      const captchaToken = await executeCaptcha();
      if (captchaToken === captchaCancelled) {
        return;
      }

      // 邮箱若绑定了 SSO 连接器,则不继续密码登录(上游同规)
      if (identifier.type === SignInIdentifier.Email) {
        const result = await checkSingleSignOn(identifier.value);

        if (result) {
          return;
        }
      }

      const [error, result] = await asyncSignIn(payload, captchaToken);

      if (error) {
        await handleError(error, {
          // 账号/密码错误、尝试次数过多(sentinel 锁定)等全部内联展示
          global: (error) => {
            setErrorMessage(error.message);
          },
        });

        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [asyncSignIn, checkSingleSignOn, executeCaptcha, handleError, redirectTo]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useEvaPasswordSignIn;
